import {
  FeeBeneficiaryTypeInterface,
  FeeCompanyInterface,
  FeeInterface,
  FeeScheduleInterface,
  FeeTransactionTypeInterface,
  FeeTypeInterface
} from '@/interfaces/FeeStructures.interface';
import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';

export class ApiFeeStructures extends BaseApiClient {
  /**
   * Get fees
   */
  async list(
    listData: ListDataParams
  ): Promise<ListDataResponse<FeeInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/fees', listData, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create or update fees
   */
  async createOrUpdateFees(
    fees: Partial<FeeInterface>[]
  ): Promise<FeeInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .put('/fees', { data: fees }, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get fee transaction types
   */
  async getFeeTransactionTypes(
    listData: ListDataParams
  ): Promise<ListDataResponse<FeeTransactionTypeInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/fee-transaction-types', listData, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create fee transaction type
   */
  async createFeeTransactionType(
    feeVersionId: number,
    payload: Partial<FeeTransactionTypeInterface>
  ): Promise<FeeTransactionTypeInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/fee-transaction-types/${feeVersionId}`, payload, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get fee beneficiary types
   */
  async getFeeBeneficiaryTypes(
    listData: ListDataParams
  ): Promise<ListDataResponse<FeeBeneficiaryTypeInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/fee-beneficiary-types', listData, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create fee beneficiary type
   */
  async createFeeBeneficiaryType(
    feeBeneficiaryType: Partial<FeeBeneficiaryTypeInterface>
  ): Promise<FeeBeneficiaryTypeInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/fee-beneficiary-types`, feeBeneficiaryType, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get fee companies
   */
  async getFeeCompanies(): Promise<FeeCompanyInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/fees/companies', {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update fee schedule
   */
  async updateFeeSchedule(
    feeScheduleId: number,
    payload: {
      amount: number;
      fee_type_id: number;
    }
  ): Promise<FeeScheduleInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/fees-schedules/${feeScheduleId}`, payload, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { feeStructuresQueryKeys } from './query-keys';
export type {
  FeeStructuresFeeBeneficiaryTypesListQueryParams,
  FeeStructuresFeesListQueryParams,
  FeeStructuresFeeTransactionTypesListQueryParams
} from './query-keys';
export {
  useFeeStructuresFeeBeneficiaryTypesListQuery,
  useFeeStructuresFeeCompaniesQuery,
  useFeeStructuresFeesListQuery,
  useFeeStructuresFeeTransactionTypesListQuery
} from './use-fee-structures-queries';
export {
  useFeeStructuresCreateFeeBeneficiaryTypeMutation,
  useFeeStructuresCreateFeeTransactionTypeMutation,
  useFeeStructuresCreateOrUpdateFeesMutation,
  useFeeStructuresUpdateFeeScheduleMutation
} from './use-fee-structures-mutations';
export type {
  FeeStructuresCreateFeeTransactionTypeVariables,
  FeeStructuresUpdateFeeScheduleVariables
} from './use-fee-structures-mutations';
